import { requiredString } from "@frontend-shared/schema/field-builders";
import type { IChatListObject } from "@frontend-shared/types";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { StyledInput } from "@/components/StyledInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { FORM_VALIDATION_OPTIONS } from "@/lib/validations/form-defaults";

interface IRenameChatDialogProps {
  // NOTE: NSAI-6473 — only the title is read, so the dialog takes the fields it
  // uses rather than a whole sidebar row. The chat page opens the same dialog
  // from its header, where the chat comes back from `useGetChatByUuid` as an
  // `IChat` and has no sidebar-only fields to give.
  chat: Pick<IChatListObject, "uuid" | "title">;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onRename: (newTitle: string) => void;
}

export const RenameChatDialog: React.FC<IRenameChatDialogProps> = ({
  chat,
  isOpen,
  onOpenChange,
  onRename,
}) => {
  const { t } = useTranslation("sidebar");

  const formSchema = z.object({
    title: requiredString({
      requiredMessage: t("chat_list.rename_chat.required"),
    }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    ...FORM_VALIDATION_OPTIONS,
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: chat.title,
    },
  });

  useEffect(() => {
    if (isOpen) {
      form.reset({ title: chat.title });
    }
  }, [isOpen, chat.title, form]);

  const handleRename = (values: z.infer<typeof formSchema>) => {
    if (values.title.trim() && values.title !== chat.title) {
      onRename(values.title);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent data-testid="rename-chat-dialog">
        <DialogHeader>
          <DialogTitle data-testid="rename-chat-title">
            {t("chat_list.rename_chat.title")}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleRename)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field, fieldState: { error } }) => (
                <FormItem>
                  <FormControl>
                    <StyledInput
                      {...field}
                      isError={!!error}
                      data-testid="rename-chat-title-input"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          form.handleSubmit(handleRename)();
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage data-testid="rename-chat-title-input-error">
                    {form.formState.errors.title?.message}
                  </FormMessage>
                </FormItem>
              )}
            />
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                data-testid="rename-chat-cancel-button"
                onClick={() => onOpenChange(false)}
              >
                {t("chat_list.rename_chat.cancel")}
              </Button>
              <Button
                type="submit"
                data-testid="rename-chat-confirm-button"
                disabled={!form.formState.isDirty}
              >
                {t("chat_list.rename_chat.rename")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
